import { useEffect, useState } from 'react'
import ContactEditForm from './ContactEditForm'
import { buildContactPatch } from '../../utils/contactEditPatch'
import { toDobDisplayValue } from '../../utils/contactFields'

const DRAFT_KEYS = ['name', 'phone', 'email', 'address', 'street', 'ward', 'district', 'province', 'latitude', 'longitude', 'gender', 'dob', 'stage', 'notes']
const NUMERIC_KEYS = ['latitude', 'longitude']

function buildDraft(contact) {
  const draft = {}
  for (const key of DRAFT_KEYS) {
    if (key === 'dob') {
      draft.dob = toDobDisplayValue(contact?.dob)
      continue
    }
    draft[key] = NUMERIC_KEYS.includes(key)
      ? (contact?.[key] ?? '').toString()
      : (contact?.[key] ?? '')
  }
  return draft
}

export default function EditContactModal({ open, onClose, contact, fields, t, onSave }) {
  const [draft, setDraft] = useState(() => buildDraft(contact))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) setDraft(buildDraft(contact))
  }, [open, contact])

  if (!open) return null

  async function handleSave() {
    const { patch, error } = buildContactPatch({ original: contact || {}, draft, fields, t })
    if (error) {
      alert(error)
      return
    }
    if (Object.keys(patch).length === 0) {
      onClose()
      return
    }
    setSaving(true)
    try {
      await onSave(patch)
      onClose()
    } catch {
      alert(t?.('errors.saveFailed') || 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <span className="font-bold text-gray-900 text-base">{t?.('contacts.editContact') || 'Edit contact'}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">
          <ContactEditForm fields={fields} draft={draft} setDraft={setDraft} t={t} />
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="text-sm font-medium text-gray-500 hover:text-gray-700">
            {t?.('contacts.cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#1535c8] text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-50"
          >
            {saving ? (t?.('contacts.saving') || 'Saving…') : (t?.('contacts.save') || 'Save')}
          </button>
        </div>
      </div>
    </div>
  )
}
